import type { ProjectParticipationsT } from '@/components/common/menu/lib/types/navigationDataTypes.ts'
import type { User } from '@/store/user.ts'
import { activeMembership } from '@/routes/start/lib/activeMembership.ts'
import { resolveNoStageLanding } from '@/routes/start/lib/resolveNoStageLanding.ts'
import { START_PATH } from '@/routes/start/lib/startPaths.ts'

type ParticipationLicenceT = {
  projectStage?: {
    project?: { isDemo?: boolean; organization?: { id?: number } | null } | null
  } | null
}

/**
 * A participation in a demo project that the given organization OWNS. The participation's own
 * `organization` cannot tell (see landingParticipations), so the project's organization decides.
 */
export function isOwnDemoParticipation(
  participation: ParticipationLicenceT,
  organizationId: number,
): boolean {
  return (
    participation.projectStage?.project?.isDemo === true &&
    participation.projectStage?.project?.organization?.id === organizationId
  )
}

/**
 * The participations the post-login landing may pick a stage from
 * (docs/plans/model-licencji.md, "Kreator `/start` po demo").
 *
 * An owner whose only stages are their own organization's demo, and who would otherwise be
 * sent to the `/start` wizard, lands in the wizard rather than in the demo: the demo is a
 * preview, not the company's first project. Everyone else keeps the usual choice — in
 * particular an outside participant whose only project is someone else's demo, and anyone
 * with at least one other stage. A direct link to a demo stage never goes through here.
 *
 * "Own" is decided by the organization that OWNS the project. The participation's own
 * `organization` cannot tell: it is the participant's company, and /users/me/
 * project_participations lists only the active organization's participations, so it always
 * equals the active organization — an outsider's included.
 *
 * `isDemo` is serialized on the owner's own participation list only, and a list cached by a
 * build that predates it simply has no demo to skip.
 */
export function landingParticipations(
  participations: ProjectParticipationsT | undefined,
  user: User,
): ProjectParticipationsT | undefined {
  if (!participations?.length) return participations
  if (resolveNoStageLanding(user) !== START_PATH) return participations

  const organizationId = activeMembership(user)?.organization?.id
  if (organizationId == null) return participations

  return (participations as ParticipationLicenceT[]).every(participation =>
    isOwnDemoParticipation(participation, organizationId),
  )
    ? ([] as unknown as ProjectParticipationsT)
    : participations
}
